import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { BetaAccessGate } from "@/components/beta-access-gate";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { parseErrorMessage } from "@/lib/queryClient";
import { DollarSign, Percent, Wrench, Leaf, Droplets, Zap, MoreHorizontal, Users, CalendarClock, FileText, Upload, X, Landmark, CheckCircle2, AlertTriangle } from "lucide-react";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import type { PmFeeSettings, OwnerInvoiceConfig } from "@shared/schema";

// String-typed mirror of OwnerInvoiceConfig for controlled inputs.
type InvoiceConfigForm = {
  companyName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  email: string;
  phone: string;
  logoUrl: string;
  footerNotes: string;
  paymentInstructions: string;
};

const EMPTY_INVOICE_CONFIG: InvoiceConfigForm = {
  companyName: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postalCode: "",
  email: "",
  phone: "",
  logoUrl: "",
  footerNotes: "",
  paymentInstructions: "",
};

function toInvoiceConfigForm(cfg: OwnerInvoiceConfig | null | undefined): InvoiceConfigForm {
  if (!cfg) return EMPTY_INVOICE_CONFIG;
  return {
    companyName: cfg.companyName ?? "",
    addressLine1: cfg.addressLine1 ?? "",
    addressLine2: cfg.addressLine2 ?? "",
    city: cfg.city ?? "",
    state: cfg.state ?? "",
    postalCode: cfg.postalCode ?? "",
    email: cfg.email ?? "",
    phone: cfg.phone ?? "",
    logoUrl: cfg.logoUrl ?? "",
    footerNotes: cfg.footerNotes ?? "",
    paymentInstructions: cfg.paymentInstructions ?? "",
  };
}

// Empty companyName means "not configured" → save null so renderers fall back
// to the generic header. Optional fields are dropped when blank.
function toInvoiceConfigPayload(form: InvoiceConfigForm): OwnerInvoiceConfig | null {
  if (!form.companyName.trim()) return null;
  const opt = (v: string) => (v.trim() ? v.trim() : undefined);
  return {
    companyName: form.companyName.trim(),
    addressLine1: form.addressLine1.trim(),
    addressLine2: opt(form.addressLine2),
    city: form.city.trim(),
    state: form.state.trim(),
    postalCode: form.postalCode.trim(),
    email: form.email.trim(),
    phone: opt(form.phone),
    logoUrl: opt(form.logoUrl),
    footerNotes: opt(form.footerNotes),
    paymentInstructions: opt(form.paymentInstructions),
  };
}

type OwnerEffectiveRate = {
  ownerId: number;
  ownerName: string;
  mgmtFeeType: "flat" | "percent";
  mgmtFeeAmount: number;
  vendorUpchargePercent: number;
  categoryUpcharges: Record<string, number>;
  source: "default" | "override";
};

const CATEGORIES = [
  { key: "maintenance", label: "Maintenance", icon: Wrench },
  { key: "cleaning", label: "Cleaning", icon: MoreHorizontal },
  { key: "landscaping", label: "Landscaping", icon: Leaf },
  { key: "plumbing", label: "Plumbing", icon: Droplets },
  { key: "hvac", label: "HVAC", icon: Zap },
  { key: "electrical", label: "Electrical", icon: Zap },
  { key: "other", label: "Other", icon: MoreHorizontal },
] as const;

export default function AppAccountPmBilling() {
  const { toast } = useToast();

  const { data: settings, isLoading } = useQuery<PmFeeSettings | null>({
    queryKey: ["/api/app/account/pm-fee-settings"],
  });

  const { data: effectiveRates, isLoading: ratesLoading } = useQuery<OwnerEffectiveRate[]>({
    queryKey: ["/api/app/account/pm-fee-settings/effective-rates"],
  });

  const { data: mercuryStatus } = useQuery<{ configured: boolean }>({
    queryKey: ["/api/app/account/mercury-ar-status"],
  });

  const [mgmtFeeType, setMgmtFeeType] = useState<"flat" | "percent">("percent");
  const [mgmtFeeAmount, setMgmtFeeAmount] = useState("0");
  const [vendorUpchargePercent, setVendorUpchargePercent] = useState("0");
  const [categoryUpcharges, setCategoryUpcharges] = useState<Record<string, string>>({});
  const [overdueRemindersEnabled, setOverdueRemindersEnabled] = useState(true);
  const [ownerCallCalendarLink, setOwnerCallCalendarLink] = useState("");
  const [mercuryInvoicingEnabled, setMercuryInvoicingEnabled] = useState(true);
  const [mercuryDispatchMode, setMercuryDispatchMode] = useState<"mercury_email" | "link_only">("mercury_email");
  const [invoiceConfig, setInvoiceConfig] = useState<InvoiceConfigForm>(EMPTY_INVOICE_CONFIG);
  const [logoUploading, setLogoUploading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  if (!isLoading && !initialized && settings !== undefined) {
    setMgmtFeeType((settings?.mgmtFeeType as "flat" | "percent") ?? "percent");
    setMgmtFeeAmount(settings?.mgmtFeeAmount ?? "0");
    setVendorUpchargePercent(settings?.vendorUpchargePercent ?? "0");
    const cats: Record<string, string> = {};
    if (settings?.categoryUpcharges) {
      for (const [k, v] of Object.entries(settings.categoryUpcharges)) {
        cats[k] = String(v);
      }
    }
    setCategoryUpcharges(cats);
    setOverdueRemindersEnabled(settings?.overdueRemindersEnabled ?? true);
    setOwnerCallCalendarLink(settings?.ownerCallCalendarLink ?? "");
    setInvoiceConfig(toInvoiceConfigForm(settings?.ownerInvoiceConfig));
    setMercuryInvoicingEnabled(settings?.mercuryInvoicingEnabled ?? true);
    setMercuryDispatchMode((settings?.mercuryDispatchMode as "mercury_email" | "link_only") ?? "mercury_email");
    setInitialized(true);
  }

  const setConfigField = (field: keyof InvoiceConfigForm) => (value: string) =>
    setInvoiceConfig((prev) => ({ ...prev, [field]: value }));

  async function handleLogoUpload(file: File) {
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      toast({ title: "Logo must be a PNG or JPEG image", variant: "destructive" });
      return;
    }
    setLogoUploading(true);
    try {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("File read failed"));
        reader.readAsDataURL(file);
      });
      const res = await apiRequest("POST", "/api/app/upload", {
        filename: file.name,
        mimeType: file.type,
        data: dataUrl.split(",")[1],
      });
      const data = await res.json();
      setConfigField("logoUrl")(data.imageUrl as string);
    } catch (err) {
      toast({ title: "Logo upload failed", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });
    } finally {
      setLogoUploading(false);
    }
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const catNums: Record<string, number> = {};
      for (const [k, v] of Object.entries(categoryUpcharges)) {
        const n = parseFloat(v);
        if (!isNaN(n) && n >= 0) catNums[k] = n;
      }
      return apiRequest("PUT", "/api/app/account/pm-fee-settings", {
        mgmtFeeType,
        mgmtFeeAmount: parseFloat(mgmtFeeAmount) || 0,
        vendorUpchargePercent: parseFloat(vendorUpchargePercent) || 0,
        categoryUpcharges: catNums,
        overdueRemindersEnabled,
        ownerCallCalendarLink: ownerCallCalendarLink.trim() || null,
        ownerInvoiceConfig: toInvoiceConfigPayload(invoiceConfig),
        mercuryInvoicingEnabled,
        mercuryDispatchMode,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/account/pm-fee-settings"] });
      toast({ title: "Owner Settings saved" });
    },
    onError: (err) => {
      toast({ title: "Failed to save", description: parseErrorMessage(err, ""), variant: "destructive" });
    },
  });

  return (
    <AppLayout>
      <BetaAccessGate featureKey="owner_portal" featureName="Owner Portal & CRM">
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1
              className="text-2xl font-bold text-gray-900 dark:text-gray-100"
              data-tour="pm-billing-heading"
            >
              Owner Settings
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Set your default management fee structure and vendor upcharge rates. These become the account-wide defaults for all owners; individual owners can be overridden in the Owners CRM.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="pm-billing" />
            <GuideButton pageKey="pm-billing" />
          </div>
        </div>

        <GuideNudgeBanner pageKey="pm-billing" />

        {isLoading ? (
          <Card>
            <CardContent className="p-6 space-y-4">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
        ) : (
          <>
            <Card data-tour="pm-billing-mgmt-fee">
              <CardHeader>
                <CardTitle className="text-base">Management Fee</CardTitle>
                <CardDescription>
                  Applied per property per billing cycle. Choose a flat dollar amount or a percentage of gross collected rent.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Fee Type</Label>
                  <Select value={mgmtFeeType} onValueChange={(v) => setMgmtFeeType(v as "flat" | "percent")}>
                    <SelectTrigger data-testid="select-mgmt-fee-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Percent of gross revenue (%)</SelectItem>
                      <SelectItem value="flat">Flat amount per property ($/mo)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{mgmtFeeType === "percent" ? "Percentage" : "Flat Amount"}</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400">
                      {mgmtFeeType === "percent" ? <Percent className="h-4 w-4" /> : <DollarSign className="h-4 w-4" />}
                    </div>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={mgmtFeeAmount}
                      onChange={(e) => setMgmtFeeAmount(e.target.value)}
                      className="pl-9"
                      data-testid="input-mgmt-fee-amount"
                    />
                  </div>
                  <p className="text-xs text-gray-400">
                    {mgmtFeeType === "percent"
                      ? "Enter a number between 0 and 100. E.g. 10 = 10% of gross rent."
                      : "Enter the dollar amount charged per property per month."}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card data-tour="pm-billing-vendor-upcharge">
              <CardHeader>
                <CardTitle className="text-base">Vendor Upcharge</CardTitle>
                <CardDescription>
                  Applied on top of vendor invoices before they appear on owner statements. Set a global default and optionally override per category.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Global Upcharge (%)</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400">
                      <Percent className="h-4 w-4" />
                    </div>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={vendorUpchargePercent}
                      onChange={(e) => setVendorUpchargePercent(e.target.value)}
                      className="pl-9"
                      data-testid="input-vendor-upcharge-percent"
                    />
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Category Overrides (optional)</p>
                  <p className="text-xs text-gray-400">
                    Leave blank to use the global upcharge above. Enter a value to override for that category specifically.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {CATEGORIES.map(({ key, label }) => (
                      <div key={key} className="space-y-1">
                        <Label className="text-xs">{label}</Label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-2 flex items-center pointer-events-none text-gray-400">
                            <Percent className="h-3 w-3" />
                          </div>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="—"
                            value={categoryUpcharges[key] ?? ""}
                            onChange={(e) =>
                              setCategoryUpcharges((prev) => ({ ...prev, [key]: e.target.value }))
                            }
                            className="pl-7 h-8 text-sm"
                            data-testid={`input-category-upcharge-${key}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Overdue Invoice Reminders</CardTitle>
                <CardDescription>
                  When on, owners are automatically emailed a reminder once their invoice becomes overdue, repeating at most once every 3 days until it's paid. You can also mute reminders for an individual invoice from the Owner Invoices page.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between gap-4">
                  <Label htmlFor="switch-overdue-reminders" className="text-sm font-medium">
                    Send overdue reminder emails
                  </Label>
                  <Switch
                    id="switch-overdue-reminders"
                    checked={overdueRemindersEnabled}
                    onCheckedChange={setOverdueRemindersEnabled}
                    data-testid="switch-overdue-reminders"
                  />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="card-owner-call-link">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CalendarClock className="h-4 w-4" />
                  Book-a-Call Link
                </CardTitle>
                <CardDescription>
                  Paste your scheduling link (Google Calendar, Calendly, or any URL). A "Book a call with your PM" button appears on every owner's Messages page in their portal, opening this link in a new tab. Leave blank to hide it.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="input-owner-call-link">Scheduling URL</Label>
                  <Input
                    id="input-owner-call-link"
                    type="url"
                    inputMode="url"
                    placeholder="https://calendly.com/your-name/intro-call"
                    value={ownerCallCalendarLink}
                    onChange={(e) => setOwnerCallCalendarLink(e.target.value)}
                    data-testid="input-owner-call-link"
                  />
                  <p className="text-xs text-gray-400">
                    Must start with http:// or https://. Leave empty to remove the button for all owners.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="card-mercury-status">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Landmark className="h-4 w-4" />
                  Invoice Payments (Mercury)
                </CardTitle>
                <CardDescription>
                  Owner invoices are collected through Mercury's hosted invoicing. When connected, every invoice you send includes a "Pay Invoice" link in the owner's portal so they can pay online; when not connected, invoices go out by email and owners pay you directly.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {mercuryStatus?.configured ? (
                  <>
                    <div className="flex items-start gap-2 rounded-lg bg-green-50 dark:bg-green-950/40 p-3" data-testid="mercury-status-connected">
                      <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                      <div className="text-sm">
                        <p className="font-medium text-green-800 dark:text-green-300">Connected</p>
                        <p className="text-green-700 dark:text-green-400 text-xs mt-0.5">
                          Your Mercury account is linked and ready to collect owner invoices.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <Label htmlFor="switch-mercury-invoicing" className="text-sm font-medium">
                          Use Mercury for owner invoices
                        </Label>
                        <p className="text-xs text-gray-400 mt-0.5">
                          When off, invoices are sent by email only and owners pay you directly (no Pay Invoice button).
                        </p>
                      </div>
                      <Switch
                        id="switch-mercury-invoicing"
                        checked={mercuryInvoicingEnabled}
                        onCheckedChange={setMercuryInvoicingEnabled}
                        data-testid="switch-mercury-invoicing"
                      />
                    </div>

                    {mercuryInvoicingEnabled && (
                      <div className="space-y-2">
                        <Label>Invoice delivery</Label>
                        <Select
                          value={mercuryDispatchMode}
                          onValueChange={(v) => setMercuryDispatchMode(v as "mercury_email" | "link_only")}
                        >
                          <SelectTrigger data-testid="select-mercury-dispatch-mode">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mercury_email">Mercury emails the invoice to the owner</SelectItem>
                            <SelectItem value="link_only">Create only — owner pays via the portal's Pay Invoice button</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-400">
                          {mercuryDispatchMode === "mercury_email"
                            ? "Mercury sends its own invoice email (with payment reminders) in addition to the owner's portal notification."
                            : "No Mercury email — the hosted invoice is only linked to the Pay Invoice button in the owner's portal."}
                        </p>
                      </div>
                    )}
                    <p className="text-xs text-gray-400">
                      Remember to press Save Owner Settings below to apply changes.
                    </p>
                  </>
                ) : (
                  <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-950/40 p-3" data-testid="mercury-status-not-connected">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800 dark:text-amber-300">Not connected</p>
                      <p className="text-amber-700 dark:text-amber-400 text-xs mt-0.5">
                        Invoices currently fall back to email, and owners see "Payment link not yet available" instead of a Pay Invoice button. To enable online payment, connect your Mercury account by adding a Mercury Invoicing (AR) API token to this deployment's secrets as <code className="font-mono">MERCURY_AR_API_TOKEN</code> — generate one in Mercury under Settings → API Tokens, or contact PadPulse support and we'll walk you through it.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card data-testid="card-invoice-details">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Invoice Details
                </CardTitle>
                <CardDescription>
                  Your company identity as it appears on owner invoices — the preview, downloadable PDF, owner portal, and invoice emails all use this header. Leave the company name blank to use the generic header.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="input-invoice-company-name">Company Name</Label>
                  <Input
                    id="input-invoice-company-name"
                    placeholder="Acme Property Management LLC"
                    value={invoiceConfig.companyName}
                    onChange={(e) => setConfigField("companyName")(e.target.value)}
                    data-testid="input-invoice-company-name"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Address Line 1</Label>
                    <Input
                      value={invoiceConfig.addressLine1}
                      onChange={(e) => setConfigField("addressLine1")(e.target.value)}
                      data-testid="input-invoice-address1"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Address Line 2 (optional)</Label>
                    <Input
                      value={invoiceConfig.addressLine2}
                      onChange={(e) => setConfigField("addressLine2")(e.target.value)}
                      data-testid="input-invoice-address2"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">City</Label>
                    <Input
                      value={invoiceConfig.city}
                      onChange={(e) => setConfigField("city")(e.target.value)}
                      data-testid="input-invoice-city"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">State</Label>
                      <Input
                        value={invoiceConfig.state}
                        onChange={(e) => setConfigField("state")(e.target.value)}
                        data-testid="input-invoice-state"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Postal Code</Label>
                      <Input
                        value={invoiceConfig.postalCode}
                        onChange={(e) => setConfigField("postalCode")(e.target.value)}
                        data-testid="input-invoice-postal"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Billing Email</Label>
                    <Input
                      type="email"
                      value={invoiceConfig.email}
                      onChange={(e) => setConfigField("email")(e.target.value)}
                      data-testid="input-invoice-email"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Phone (optional)</Label>
                    <Input
                      type="tel"
                      value={invoiceConfig.phone}
                      onChange={(e) => setConfigField("phone")(e.target.value)}
                      data-testid="input-invoice-phone"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Logo (optional)</Label>
                  {invoiceConfig.logoUrl ? (
                    <div className="flex items-center gap-3">
                      <img
                        src={invoiceConfig.logoUrl}
                        alt="Invoice logo"
                        className="h-12 max-w-[160px] object-contain rounded border border-gray-200 dark:border-gray-700 bg-white"
                        data-testid="img-invoice-logo-preview"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfigField("logoUrl")("")}
                        data-testid="button-remove-invoice-logo"
                      >
                        <X className="h-4 w-4 mr-1" /> Remove
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <input
                        id="input-invoice-logo-file"
                        type="file"
                        accept="image/png,image/jpeg"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) void handleLogoUpload(file);
                          e.target.value = "";
                        }}
                        data-testid="input-invoice-logo-file"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={logoUploading}
                        onClick={() => document.getElementById("input-invoice-logo-file")?.click()}
                        data-testid="button-upload-invoice-logo"
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        {logoUploading ? "Uploading…" : "Upload logo"}
                      </Button>
                      <p className="text-xs text-gray-400 mt-1">PNG or JPEG. Shown at the top of invoices.</p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="textarea-invoice-payment-instructions">Payment Instructions (optional)</Label>
                  <Textarea
                    id="textarea-invoice-payment-instructions"
                    rows={2}
                    placeholder="e.g. Pay via the secure link on your invoice, or mail checks to the address above."
                    value={invoiceConfig.paymentInstructions}
                    onChange={(e) => setConfigField("paymentInstructions")(e.target.value)}
                    data-testid="textarea-invoice-payment-instructions"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="textarea-invoice-footer-notes">Footer Notes (optional)</Label>
                  <Textarea
                    id="textarea-invoice-footer-notes"
                    rows={2}
                    placeholder="Shown under the invoice totals — thank-you note, terms, etc."
                    value={invoiceConfig.footerNotes}
                    onChange={(e) => setConfigField("footerNotes")(e.target.value)}
                    data-testid="textarea-invoice-footer-notes"
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
                data-testid="button-save-pm-billing"
                data-tour="pm-billing-save"
              >
                {saveMutation.isPending ? "Saving…" : "Save Owner Settings"}
              </Button>
            </div>

            <Card data-testid="card-effective-rates" data-tour="pm-billing-effective-rates">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Effective Rates by Owner
                </CardTitle>
                <CardDescription>
                  The rates actually charged for each owner — your defaults above, merged with any per-owner override set in the Owners CRM. Save your changes to refresh this preview.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {ratesLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ) : !effectiveRates || effectiveRates.length === 0 ? (
                  <p className="text-sm text-gray-400" data-testid="text-no-owners">
                    No owners yet. Add owners in the Owners CRM to see their effective rates here.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b">
                          <th className="py-2 pr-4 font-medium">Owner</th>
                          <th className="py-2 pr-4 font-medium">Mgmt Fee</th>
                          <th className="py-2 pr-4 font-medium">Vendor Upcharge</th>
                          <th className="py-2 font-medium">Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {effectiveRates.map((rate) => (
                          <tr key={rate.ownerId} className="border-b last:border-0" data-testid={`row-owner-rate-${rate.ownerId}`}>
                            <td className="py-2 pr-4 font-medium text-gray-900 dark:text-gray-100" data-testid={`text-owner-name-${rate.ownerId}`}>
                              {rate.ownerName}
                            </td>
                            <td className="py-2 pr-4" data-testid={`text-owner-mgmt-fee-${rate.ownerId}`}>
                              {rate.mgmtFeeType === "percent"
                                ? `${rate.mgmtFeeAmount}%`
                                : `$${rate.mgmtFeeAmount}/mo`}
                            </td>
                            <td className="py-2 pr-4" data-testid={`text-owner-upcharge-${rate.ownerId}`}>
                              {rate.vendorUpchargePercent}%
                              {rate.categoryUpcharges && Object.keys(rate.categoryUpcharges).length > 0 && (
                                <span className="text-xs text-gray-400"> (+ category overrides)</span>
                              )}
                            </td>
                            <td className="py-2" data-testid={`text-owner-source-${rate.ownerId}`}>
                              {rate.source === "override" ? (
                                <span className="text-brand text-xs font-medium">Custom override</span>
                              ) : (
                                <span className="text-gray-400 text-xs">Account default</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
      </BetaAccessGate>
    </AppLayout>
  );
}
